import { useState, useEffect, useRef } from "react"
import { Link } from "@tanstack/react-router"
import { Sparkles, CheckCircle2, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import NewTransitLines from "@/components/common/NewTransitLines"

const DUMMY_SCENARIOS = [
  {
    query: "I want silk fabric 250gsm, 500 metres, delivery in Delhi, budget ₹350/metre",
    rfqId: "RFQ-2026-8821",
    status: "Analyzing Market",
    suppliers: [
      { name: "Delhi Weaves Ltd", details: "Match 97% • Verified Supplier • Delhi" },
      { name: "Vardhman Textiles", details: "Match 94% • Premium Partner • Ludhiana" }
    ]
  },
  {
    query: "Looking for 100x monocrystalline solar panels 450W, delivery to Mumbai port, target delivery 15 days",
    rfqId: "RFQ-2026-9104",
    status: "Matching Suppliers",
    suppliers: [
      { name: "Sova Solar India", details: "Match 98% • Verified Manufacturer • Kolkata" },
      { name: "Waaree Energies", details: "Match 95% • Tier-1 Supplier • Surat" }
    ]
  },
  {
    query: "Need 5,000 biodegradable jute shopping bags, printed logo, delivery to Bengaluru warehouse by next month",
    rfqId: "RFQ-2026-1052",
    status: "Generating RFQ",
    suppliers: [
      { name: "GreenJute Exports", details: "Match 99% • Eco-Certified • Kolkata" },
      { name: "Bengaluru Packaging Co", details: "Match 93% • Local Supplier • Bengaluru" }
    ]
  }
]

export function Hero() {
  const [queryIndex, setQueryIndex] = useState(0)
  const [phase, setPhase] = useState<'typing' | 'analyzing' | 'matched'>('typing')
  const [displayedText, setDisplayedText] = useState("")
  const [matchStep, setMatchStep] = useState(0)
  const cardRef = useRef<HTMLDivElement>(null)

  const currentScenario = DUMMY_SCENARIOS[queryIndex]

  // Auto scroll to bottom when contents change/grow
  useEffect(() => {
    if (cardRef.current) {
      cardRef.current.scrollTo({
        top: cardRef.current.scrollHeight,
        behavior: "smooth"
      })
    }
  }, [phase, displayedText, matchStep])

  // Manage sequential steps in matched phase
  useEffect(() => {
    if (phase === 'typing') {
      setMatchStep(0)
    } else if (phase === 'analyzing') {
      setMatchStep(1)
    } else if (phase === 'matched') {
      setMatchStep(2) // Fills progress bar to 100%
      const t1 = setTimeout(() => setMatchStep(3), 1000) // Suppliers list fades in
      const t2 = setTimeout(() => setMatchStep(4), 2200) // CTA link fades in
      return () => {
        clearTimeout(t1)
        clearTimeout(t2)
      }
    }
  }, [phase])

  // Handle auto-typing
  useEffect(() => {
    if (phase === 'typing') {
      setDisplayedText("")
      let charIndex = 0
      const queryText = currentScenario.query
      const interval = setInterval(() => {
        setDisplayedText(queryText.slice(0, charIndex + 1))
        charIndex++
        if (charIndex >= queryText.length) {
          clearInterval(interval)
          const timeout = setTimeout(() => {
            setPhase('analyzing')
          }, 1800)
          return () => clearTimeout(timeout)
        }
      }, 80)

      return () => clearInterval(interval)
    }
  }, [phase, queryIndex, currentScenario.query])

  // Handle phase progression
  useEffect(() => {
    if (phase === 'analyzing') {
      const timeout = setTimeout(() => {
        setPhase('matched')
      }, 4000)
      return () => clearTimeout(timeout)
    } else if (phase === 'matched') {
      const timeout = setTimeout(() => {
        setPhase('typing')
        setQueryIndex((prev) => (prev + 1) % DUMMY_SCENARIOS.length)
      }, 10000)
      return () => clearTimeout(timeout)
    }
  }, [phase])

  const handleScrollTo = (id: string) => {
    const el = document.querySelector(id)
    if (el) {
      el.scrollIntoView({ behavior: "smooth" })
    }
  }

  return (
    <section className="relative overflow-hidden bg-surface py-8 md:py-12 lg:py-14">
      {/* Dynamic Animated Transit Lines Background aligned with theme */}
      <NewTransitLines variant="full" />

      {/* Background radial highlight */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.15] grid-bg"
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-start relative z-10">
        
        {/* Left Column Wrapper */}
        <div className="w-full flex justify-center md:justify-start items-start">
          {/* Inner Content Aligned to Column Start */}
          <div className="flex flex-col gap-5 max-w-lg w-full items-start text-left">
            <h1 className="font-sans text-4xl md:text-5xl lg:text-6xl font-black text-on-surface tracking-tight leading-tight select-none">
              Source anything.<br />From anywhere.
            </h1>
            <p className="text-secondary text-base md:text-lg max-w-[480px]">
              Describe what you need in plain language<br /> Enga finds verified suppliers, manages your RFQ, and handles everything until delivery.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Button
                className="bg-primary text-white hover:bg-primary-container px-6 py-3 h-auto rounded-lg text-sm font-semibold transition-all active:scale-95 cursor-pointer"
                asChild
              >
                <Link to="/signup">Submit your first RFQ</Link>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleScrollTo("#how-it-works")}
                className="bg-transparent border-05 border-primary text-primary px-6 py-3 h-auto rounded-lg text-sm font-semibold transition-all active:scale-95 hover:bg-surface-container-low hover:text-primary cursor-pointer"
              >
                Learn how it works
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column — AI Search Mockup */}
        <div className="relative max-w-md mx-auto md:max-w-none w-full group/mockup">
          {/* Main Card with fixed dimensions & scrollable content */}
          <div ref={cardRef} className="bg-white p-6 rounded-xl flex flex-col gap-4 relative z-10 h-[390px] overflow-y-auto scrollbar-none select-none transition-all duration-500 border-05 border-outline-variant shadow-[0_12px_30px_-10px_rgba(0,0,0,0.06),0_15px_40px_rgba(190,0,30,0.02)] hover:border-primary hover:shadow-[0_20px_40px_-8px_rgba(0,0,0,0.12),0_24px_50px_rgba(190,0,30,0.08)] hover:scale-[1.015]">
            
            <div className="flex items-center justify-between border-b border-outline-variant/60 pb-3">
              <div className="flex items-center gap-2 text-primary font-sans">
                <Sparkles className="size-4 fill-primary animate-pulse" />
                <span className="text-xs font-semibold uppercase tracking-wider">AI Procurement Engine</span>
              </div>
            </div>
            
            {/* Input Phase Box */}
            <div className="bg-surface-container-low p-4 rounded-lg border-05 border-primary min-h-[80px] flex items-center relative overflow-hidden transition-colors duration-300 group-hover/mockup:border-primary/60 group-hover/mockup:bg-surface-container-high">
              <p className="text-on-surface text-sm italic font-medium leading-relaxed">
                {displayedText}
                {phase === 'typing' && (
                  <span className="inline-block w-1.5 h-4 ml-0.5 bg-primary animate-blink" />
                )}
              </p>
            </div>

            {/* AI Status Area */}
            <div className="flex flex-col gap-3 pt-1">
              <div className="flex justify-between items-center border-b border-outline-variant/60 pb-2">
                <span className="text-secondary text-xs">Requirement ID</span>
                <span className="font-mono font-semibold text-xs text-on-surface">
                  {currentScenario.rfqId}
                </span>
              </div>
              
              <div className="flex justify-between items-center border-b border-outline-variant/60 pb-2">
                <span className="text-secondary text-xs">Processing Status</span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all duration-300 animate-pulse border border-primary ${
                  phase === 'typing' ? 'bg-surface text-secondary' :
                  phase === 'analyzing' ? 'bg-primary/5 text-primary ring-4 ring-primary/10' :
                  'bg-primary text-white ring-4 ring-primary/20'
                }`}>
                  {/* Glowing dynamic dot indicator */}
                  {phase === 'analyzing' && (
                    <span className="relative flex h-2 w-2 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                    </span>
                  )}
                  {phase === 'matched' && (
                    <span className="relative flex h-2 w-2 shrink-0">
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                    </span>
                  )}
                  {phase === 'typing' && (
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/40 shrink-0"></span>
                  )}
                  
                  {phase === 'typing' ? 'Waiting for input...' :
                   phase === 'analyzing' ? currentScenario.status :
                   'Analysis Complete'}
                </span>
              </div>

              {/* Progress bar for analysis (continuous loading until CTA appears) */}
              {(phase === 'analyzing' || (phase === 'matched' && matchStep < 4)) && (
                <div className="w-full bg-surface-container-highest h-1 rounded-full overflow-hidden mt-1 relative">
                  <div
                    className="absolute h-full w-[30%] bg-primary rounded-full"
                    style={{ animation: "slideBar 1.5s infinite ease-in-out" }}
                  />
                  <style>{`
                    @keyframes slideBar {
                      0% { left: -30%; }
                      100% { left: 100%; }
                    }
                  `}</style>
                </div>
              )}

              {/* Match Phase Box: Suppliers and Logistics */}
              {phase === 'matched' && (
                <div className="flex flex-col gap-3">
                  
                  {/* Suppliers List */}
                  {matchStep >= 3 && (
                    <div className="flex flex-col gap-2 animate-fade-in">
                      <span className="text-xs font-semibold text-secondary">Verified Matches Found</span>
                      <div className="flex flex-col gap-2">
                        {currentScenario.suppliers.map((sup, idx) => (
                          <div 
                            key={idx} 
                            className="bg-surface-container-low p-3 rounded-lg border border-outline-variant/60 flex flex-col gap-0.5 hover:border-primary/30 transition-all duration-200"
                          >
                            <span className="text-sm font-bold text-on-surface flex items-center gap-1.5">
                              <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                              {sup.name}
                            </span>
                            <span className="text-xs text-secondary/40 blur-[3px] select-none transition-all duration-300 hover:blur-none hover:text-secondary/70">
                              {sup.details}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* CTA overlay inside card */}
                  {matchStep >= 4 && (
                    <Link 
                      to="/signup" 
                      className="mt-1 flex items-center justify-center gap-1 text-xs font-bold text-primary hover:underline group/link animate-fade-in"
                    >
                      Unlock details & submit RFQ
                      <ArrowRight className="size-3 transition-transform duration-200 group-hover/link:translate-x-0.5" />
                    </Link>
                  )}

                </div>
              )}
            </div>
          </div>

          {/* Decorative Back Card */}
          <div
            aria-hidden
            className="absolute -top-4 -right-4 w-full h-full border-05 border-outline-variant rounded-xl opacity-20 pointer-events-none"
          />
        </div>

      </div>
    </section>
  )
}